import { NavigationConfirmBottomSheet } from "@/components/NavigationConfirmBottomSheet";
import { Textarea } from "@/components/ui/textarea";
import { useNavigationBlock } from "@/hooks/useNavigationBlock";
import { renderTextWithMentions } from "@/utils/mentionHighlight";
import { useAuthStore } from "@roman/shared/store/auth.store";
import { COMMENT_VALIDATION_CONSTANTS, validateCommentInput } from "@roman/shared/utils";
import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useDebounce } from "react-use";

type CommentInputProps = {
  setSearch: (label: string) => void;
  className?: string;
  onSubmit?: () => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  clearInput?: boolean;
  autoFocus?: boolean;
  isReply?: boolean;
  isEdit?: boolean;
  initialValue?: string;
  mention?: string[] | string;
  onShowLoginModal?: () => void;
};

const DEBOUNCE_DELAY = 200; // milliseconds

// Small spinner component for submit button
const Spinner = () => (
  <span
    className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
    aria-hidden="true"
  />
);

export const CommentInput = ({
  setSearch,
  className,
  onSubmit,
  onCancel,
  isSubmitting,
  clearInput,
  autoFocus = false,
  isReply = false,
  isEdit = false,
  initialValue = "",
  mention = "",
  onShowLoginModal,
}: CommentInputProps) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [validationError, setValidationError] = useState<string>("");
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const user = useAuthStore((state) => state.user);

  // Autofocus + put caret at end when mounting
  useLayoutEffect(() => {
    if (!autoFocus || !textareaRef.current) return;
    const el = textareaRef.current;
    el.focus();
    if (initialValue) {
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [autoFocus, initialValue]);

  // Keep local state in sync with initialValue changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // If editing and focused when initialValue changes, ensure caret at end (no timer)
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (isEdit && document.activeElement === el) {
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [isEdit]);

  // Update validation error when value changes
  useEffect(() => {
    const error = validateCommentInput(value);
    setValidationError(error);
  }, [value]);

  // Debounced search hook
  useDebounce(() => setSearch(value), DEBOUNCE_DELAY, [value]);

  // Scroll to cursor position for better UX
  const scrollToCursor = useCallback(() => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const container = textareaRef.current.parentElement;
      if (container) {
        // Calculate cursor position
        const textBeforeCursor = textareaRef.current.value.substring(0, start);
        const lines = textBeforeCursor.split("\n");
        const currentLine = lines.length - 1;

        // Get actual line height from computed styles
        const computedStyle = window.getComputedStyle(textareaRef.current);
        const fontSize = Number.parseFloat(computedStyle.fontSize);
        const lineHeight = fontSize * 1.4; // leading-[1.4] from className
        const paddingTop = Number.parseFloat(computedStyle.paddingTop);
        const paddingBottom = Number.parseFloat(computedStyle.paddingBottom);
        const cursorTop = currentLine * lineHeight + paddingTop;

        // Scroll to make cursor visible with some padding
        const containerHeight = container.clientHeight;
        const scrollTop = container.scrollTop;
        const buffer = 8; // Add some buffer space

        if (cursorTop < scrollTop + buffer) {
          // Cursor is above visible area
          container.scrollTop = Math.max(0, cursorTop - buffer);
        } else if (cursorTop + lineHeight + buffer > scrollTop + containerHeight) {
          // Cursor is below visible area - ensure cursor line is fully visible with buffer
          container.scrollTop = cursorTop + lineHeight + paddingBottom + buffer - containerHeight;
        }
      }
    }
  }, []);

  // Auto-resize textarea based on content
  const handleTextareaResize = useCallback(() => {
    if (textareaRef.current) {
      // Save cursor position
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;

      textareaRef.current.style.height = "auto"; // Reset height
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${scrollHeight}px`;

      // Restore cursor position
      textareaRef.current.setSelectionRange(start, end);

      // Scroll to cursor position
      requestAnimationFrame(scrollToCursor);
    }
  }, [scrollToCursor]);

  useEffect(() => {
    if (clearInput) {
      setValue("");
      setTimeout(() => {
        handleTextareaResize();
      }, 0);
    }
  }, [clearInput, handleTextareaResize]);

  useEffect(() => {
    handleTextareaResize();
  }, [handleTextareaResize]);

  // Handle button container open/close state
  useEffect(() => {
    const shouldOpen = value.trim() !== "" || isFocused;
    setIsOpen(shouldOpen);
  }, [value, isFocused]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Only enable Enter to submit on desktop (non-touch devices)
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (e.key === "Enter" && !e.shiftKey && !isTouchDevice && onSubmit) {
      e.preventDefault();
      onSubmit();
    } else if (e.key === "Enter") {
      // For line breaks, scroll to cursor after the DOM updates
      setTimeout(() => {
        requestAnimationFrame(scrollToCursor);
      }, 0);
    }
  };

  const handleSendClick = () => {
    if (isSubmitting) return;
    if (value.trim() === "") return;
    if (validationError) return;
    if (onSubmit) {
      onSubmit();
    }
  };

  const hasUnsavedContent = value.trim().length > 0 || isEdit || isReply;
  const { isConfirmModalOpen, confirmNavigation, cancelNavigation } = useNavigationBlock({
    when: hasUnsavedContent,
  });

  return (
    <>
      <div className={`${className ?? ""} relative`.trim()}>
        {/* Container with height limit and scroll */}
        <div
          className={`relative max-h-[200px] overflow-y-auto border-[1px] rounded-md bg-[#F9F9F9] transition-colors duration-200 ${
            validationError
              ? "border-red-500 focus-within:border-red-500"
              : "hover:border-secondary/50 focus-within:border-secondary"
          }`}
        >
          {/* Overlay for text display */}
          <p className="absolute top-0 left-0 right-0 bottom-0 px-3 py-2 text-sm font-normal leading-[1.4] pt-[8.5px] pb-[11.4px] min-h-[24px] whitespace-pre-wrap break-words pointer-events-none z-0">
            {value.trim() === "" ? (
              <span className="text-light-gray">コメントをする</span>
            ) : (
              renderTextWithMentions(value, mention)
            )}
          </p>
          <Textarea
            ref={textareaRef}
            className="relative border-none bg-transparent ring-0 text-sm focus:ring-0 focus-visible:ring-0 shadow-none font-normal placeholder:text-transparent leading-[1.4] pt-[8.5px] pb-[11.4px] resize-none min-h-[24px] whitespace-pre-wrap break-words text-transparent caret-black"
            placeholder="コメントをする"
            onChange={(e) => {
              setValue(e.target.value);
              requestAnimationFrame(handleTextareaResize);
            }}
            onKeyPress={handleKeyPress}
            onFocus={() => {
              if (user) {
                setIsFocused(true);
              } else {
                onShowLoginModal?.();
              }
            }}
            onBlur={() => {
              // Add a small delay to allow button clicks to register before hiding
              setTimeout(() => setIsFocused(false), 150);
            }}
            value={value}
            disabled={isSubmitting}
            rows={1}
          />
        </div>

        {/* Character count and validation error */}
        {validationError && (
          <div className="flex justify-between items-center mt-1 px-1">
            <div className="text-xs">{validationError && <span className="text-red">{validationError}</span>}</div>
            <div
              className={`text-xs ${
                value.length > COMMENT_VALIDATION_CONSTANTS.MAX_CHARACTERS
                  ? "text-red font-medium"
                  : value.length > COMMENT_VALIDATION_CONSTANTS.MAX_CHARACTERS * 0.9
                    ? "text-orange"
                    : "text-gray"
              }`}
            >
              {value.length}/{COMMENT_VALIDATION_CONSTANTS.MAX_CHARACTERS}
            </div>
          </div>
        )}

        {/* Button container - only show when there's content or when focused */}
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isEdit
              ? "max-h-[60px] opacity-100 mt-[14px] ease-in"
              : isOpen
                ? "max-h-[60px] opacity-100 mt-[14px] ease-in"
                : "max-h-0 opacity-0 mt-0 ease-out"
          }`}
        >
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 h-[36px] text-[12px] text-black rounded-[18px] md:hover:bg-[#ECECEC] transition-colors duration-200 leading-[1.4]"
              onMouseDown={(e) => {
                // Prevent blur event from firing before click
                e.preventDefault();
              }}
              onClick={() => {
                setValue("");
                setIsFocused(false);
                setIsOpen(false);
                onCancel?.();
                setTimeout(handleTextareaResize, 0);
              }}
              disabled={isSubmitting}
            >
              キャンセル
            </button>
            <button
              type="button"
              className={`w-[84px] h-[36px] text-[12px] rounded-[18px] transition-all duration-200 ease-in leading-[1.4] flex items-center justify-center gap-2 ${
                value.trim() === "" || isSubmitting || validationError
                  ? "bg-[#ECECEC] text-gray-500 cursor-not-allowed"
                  : "bg-[#176BF2] text-white active:scale-95"
              }`}
              onMouseDown={(e) => {
                // Prevent blur event from firing before click
                e.preventDefault();
              }}
              onClick={handleSendClick}
              disabled={isSubmitting || value.trim() === "" || !!validationError}
            >
              {isSubmitting ? <Spinner /> : isReply ? "返信" : isEdit ? "保存" : "コメント"}
            </button>
          </div>
        </div>
      </div>
      <NavigationConfirmBottomSheet
        isOpen={isConfirmModalOpen}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </>
  );
};
